import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, Tab } from '@mui/material';
import { connect, ConnectedProps } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { FormattedMessage, injectIntl } from 'react-intl';

import {
  getSurvey,
  clearSurveyRecord,
  updateSections,
  createSurvey,
  updateSurvey,
  navigateTo,
  fetchTrialWithSurveyAndGroundTruth,
  fetchTrialsList,
  reportError,
} from '../redux/actions';
import SurveySetting from '../components/survey/surveyForm/SurveySetting';
import SectionEditor from '../components/survey/surveyForm/SectionEditor';
import NotificationBox from '../components/layout/NotificationBox';
import { useTrialHasStarted, setTrialHasStarted } from '../components/survey/surveyForm/useTrialHasStarted';
import ApiClient from '../loopback/apiClient';
import { SURVEY_VERSION } from '../environment';
import {
  RootState,
  SurveyPayload,
  SurveyHistory,
  Survey,
  SurveySection,
} from '../redux/types';

type MatchParams = {
  trialId: string;
};

const CreateEditSurveyView: React.FC<Props> = ({
  trialStartDate,
  survey,
  sections,
  groundTruth,
  getSurvey: _getSurvey,
  clearSurveyRecord: _clearSurveyRecord,
  updateSections: _updateSections,
  createSurvey: _createSurvey,
  updateSurvey: _updateSurvey,
  fetchTrialWithSurveyAndGroundTruth: _fetchTrialWithSurveyAndGT,
  fetchTrialsList: _fetchTrialList,
  reportError: _reportError,
  userId,
  intl,
}: Props) => {
  const navigate = useNavigate();
  const params = useParams<MatchParams>();
  const trialId = Number(params.trialId);
  const [currentTab, setCurrentTab] = useState('settings');
  const [surveyHasChanged, setSurveyHasChanged] = useState(false);
  const [saveClicked, setSaveClicked] = useState(false);
  const [gtResetWarning, setGtResetWarning] = useState(false);
  const [optionalSections, setOptionalSections] = useState<boolean>(!!survey?.optionalSections);
  const trialHasStarted = useTrialHasStarted();
  const MAX_SECTION = 10;

  const doSetSection = useCallback(() => {
    if (survey) {
      _updateSections([...survey.payload.sections]);
    }
  }, [_updateSections, survey]);

  const doCreateSurvey = () => {
    const surveyObject = {
      createdAt: new Date().toISOString(),
      payload: {
        version: SURVEY_VERSION,
        sections: [...sections],
        // here
      },
      trialId,
      version: SURVEY_VERSION,
      optionalSections,
    };

    _createSurvey(surveyObject);
  };

  const checkIfSurveyCanBeEditted = () => {
    if (!survey || !survey.payload) {
      return true;
    }
    if (survey && survey.payload.version === 1) {
      return false;
    }
    return true;
  };

  const getLatestSurveyPayload = useCallback((): SurveyPayload => {
    if (!survey && (!sections || sections.length === 0)) {
      return {} as SurveyPayload;
    }

    let payload;
    if (survey !== null) {
      payload = survey.payload;
    }

    return {
      ...payload,
      sections: [...sections],
    } as SurveyPayload;
  }, [sections, survey]);

  const doSaveSurveyHistory = (
    surveyId: number,
    oldSurveyPayload: SurveyPayload,
    updatedAt: string,
    structureChanged: boolean,
  ) => {
    const oldSurveyData = {
      createdAt: updatedAt,
      payload: oldSurveyPayload,
      surveyId,
      quipUserId: userId,
    } as SurveyHistory;
    if (structureChanged) {
      oldSurveyData.structureChanged = structureChanged;
    }

    try {
      ApiClient.saveSurveyHistory(oldSurveyData);
    } catch (e) {
      _reportError((e as Error).message);
    }
  };

  /**
   * Checks the structural shape of the currently available survey with
   * the new survey that is going to be saved as a result of update operation.
   *
   * @param {object} oldSurvey - old survey payload
   * @param {object} newSurvey - new survey payload
   * @returns {boolean} true if structure has changed, false otherwise
   */
  const equalSurveyStruture = (oldSurvey: SurveyPayload, newSurvey: SurveyPayload) => {
    if (oldSurvey.sections.length !== newSurvey.sections.length) {
      return false;
    }
    const equalSecDetail = oldSurvey.sections.every((oldSection, index) => (
      oldSection.questions.length === newSurvey.sections[index].questions.length
      && oldSection.numCases === newSurvey.sections[index].numCases
    ));
    if (!equalSecDetail) {
      return false;
    }

    const equalQuestionOptions = oldSurvey.sections.every((oldSection, sectionIndex) => (
      oldSection.questions.every((oldSurveySecQuestion, questionIndex) => {
        if (oldSurveySecQuestion.type === 'OneOf' || oldSurveySecQuestion.type === 'MultipleChoice') {
          if (!newSurvey.sections[sectionIndex].questions[questionIndex].options) {
            return false;
          }
          return oldSurveySecQuestion.options?.length
          === newSurvey.sections[sectionIndex].questions[questionIndex].options?.length;
        }
        return true;
      })
    ));
    return equalQuestionOptions;
  };

  const doUpdateSurvey = () => {
    const _survey = { ...survey } as Survey;
    const updatedAt = new Date().toISOString();
    const newPayload = getLatestSurveyPayload();
    const newSurvey = {
      ..._survey,
      updatedAt,
      payload: newPayload,
      optionalSections,
    };
    _updateSurvey(newSurvey);

    if (trialHasStarted || (groundTruth && groundTruth.length)) {
      // here if there is groundTruth and the difference between current and previous survey
      // is true, then save the history and remove the groundTruth so that
      // the groundTruth is reset.
      if (groundTruth && groundTruth.length) {
        if (gtResetWarning) {
          // remove groundtruth, save the survey history and return
          const promiseArr = groundTruth.map((gt) => ApiClient.deleteGroundTruth(gt.id));
          Promise.all(promiseArr).then(() => {
            if (survey) {
              doSaveSurveyHistory(survey.id, survey.payload, updatedAt, true);
            }
          }).catch((error) => {
            _reportError(error);
          });
          return;
        }
      }
      if (trialHasStarted && survey) {
        doSaveSurveyHistory(survey.id, survey.payload, updatedAt, false);
      }
    }
  };

  const doSave = () => {
    const surveyId = survey ? survey.id : null;
    if (surveyId) {
      doUpdateSurvey();
    } else {
      doCreateSurvey();
    }

    // reset it to false and ignore prompt when saving
    setSurveyHasChanged(false);
    setSaveClicked(true);
  };

  const checkSurveyHasChanged = () => {
    const latestSurveyPayload = getLatestSurveyPayload();
    const currentSurveyPayload = survey ? survey.payload : {};

    if (JSON.stringify(latestSurveyPayload) !== JSON.stringify(currentSurveyPayload)) {
      setSurveyHasChanged(true);
    } else {
      setSurveyHasChanged(false);
    }
  };

  useEffect(checkSurveyHasChanged, [sections, getLatestSurveyPayload, survey]);

  useEffect(() => {
    if (survey) {
      // set initial sections if exist
      doSetSection();
      setOptionalSections(!!survey.optionalSections);
    }
  }, [survey, doSetSection]);

  useEffect(() => {
    _getSurvey(trialId);
    _fetchTrialWithSurveyAndGT(trialId);
    return () => {
      _clearSurveyRecord();
    };
  }, [trialId, _getSurvey, _fetchTrialWithSurveyAndGT, _clearSurveyRecord]);

  useEffect(() => {
    if (saveClicked) {
      navigateTo('/admin', navigate);
    }
  }, [saveClicked, navigate]);

  useEffect(() => {
    // need to check undefined as false is valid
    if (!trialStartDate || trialHasStarted === undefined) {
      return;
    }
    if (moment().utc().isSameOrAfter(trialStartDate, 'day')) {
      setTrialHasStarted(true);
    } else {
      setTrialHasStarted(false);
    }
  }, [trialStartDate, trialHasStarted]);

  useEffect(() => {
    if (currentTab === 'settings' && survey && survey.payload) {
      const oldSurveyPayload = survey.payload;
      const currentSurveyPayload = getLatestSurveyPayload();
      const structureIsEqual = equalSurveyStruture(oldSurveyPayload, currentSurveyPayload);
      if (!structureIsEqual) {
        setGtResetWarning(true);
      } else {
        setGtResetWarning(false);
      }
    }
  }, [currentTab, survey, getLatestSurveyPayload]);

  useEffect(() => {
    _fetchTrialList();
  }, [_fetchTrialList]);

  const addNewSection = () => {
    const id = uuidv4();
    const newSection = {
      id,
    } as SurveySection;
    _updateSections([...sections, newSection]);
    setCurrentTab(id);
  };

  const removeSection = (id: string) => {
    const index = sections.findIndex((section) => section.id === id);
    const nextSection = sections[index + 1] || sections[index - 1];
    const nextSectionId = nextSection ? nextSection.id : 'settings';

    _updateSections(sections.filter((section) => section.id !== id));
    setCurrentTab(nextSectionId);
  };

  const updateSection = (updatedSection: SurveySection, sectionId: string) => {
    const _sections = [...sections];
    // eslint-disable-next-line arrow-body-style
    const updatedSections = _sections.map((section) => {
      return section.id === sectionId ? updatedSection : section;
    });
    _updateSections(updatedSections);
  };

  const renderSurvey = (): React.ReactNode => sections.map((section) => (
    <SectionEditor
      key={section.id}
      section={section}
      onRemove={removeSection}
      onUpdate={updateSection}
      show={section.id === currentTab}
    />
  ));

  const renderTabForNewSection = () => (
    <Tab
      label={<FormattedMessage id="createeditsurveyview.newsection" />}
      value="new-tab"
      onClick={addNewSection}
      disabled={trialHasStarted}
    />
  );

  const renderSpecificSectionTabs = () => {
    if (!sections || sections.length === 0) {
      return '';
    }

    return sections.map((section, sectionIdx) => {
      const key = `tab-section-${sectionIdx}`;
      return (
        <Tab
          key={key}
          label={section.headline || `Sektion ${sectionIdx + 1}`}
          onClick={() => setCurrentTab(section.id)}
          value={section.id}
        />
      );
    });
  };

  const renderTabs = () => (
    <div className="ph2">
      <Tabs
        value={currentTab}
        scrollButtons="auto"
        variant="scrollable"
        indicatorColor="secondary"
      >
        {renderSpecificSectionTabs()}
        {(!sections || sections.length < MAX_SECTION) && renderTabForNewSection()}
        <Tab
          key="settings-tab"
          label={<FormattedMessage id="createeditsurveyview.settings" />}
          onClick={() => setCurrentTab('settings')}
          value="settings"
        />
      </Tabs>
    </div>
  );

  const promptMessage = intl.formatMessage({ id: 'trialmanagement.exitBeforeSaving' });
  return (
    <>
      {/* todo: implement prompt logic */}
      {/* <Prompt
        when={surveyHasChanged}
        message={promptMessage}
      /> */}
      <h2><FormattedMessage id="trialmanagement.createEditSurvey" /></h2>
      {trialHasStarted && (
        <NotificationBox>
          <FormattedMessage id="createeditsurveyview.trialHasStaredMessage" />
        </NotificationBox>
      )}
      {
        gtResetWarning && (
          <NotificationBox error>
            <FormattedMessage id="createeditsurveyview.resetGroundTruth" />
          </NotificationBox>
        )
      }
      {
        !checkIfSurveyCanBeEditted() && (
          <NotificationBox error>
            <FormattedMessage id="trialmanagement.surveyEditorUnavailable" />
          </NotificationBox>
        )
      }
      {
        checkIfSurveyCanBeEditted() && renderTabs()
      }
      {
      checkIfSurveyCanBeEditted() && (
        currentTab === 'settings'
          ? (
            <SurveySetting
              doSave={doSave}
              setOptionalSections={setOptionalSections}
              optionalSections={optionalSections}
            />
          )
          : renderSurvey()
      )
      }
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  survey: state.surveyV2.survey,
  sections: state.surveyV2.sections,
  groundTruth: state.trial.trial?.survey?.groundTruth,
  trialStartDate: state.trial.trial?.startDate,
  trial: state.trial.trial,
  userId: state.users?.token?.userId,
});

const dispatchProps = {
  getSurvey,
  fetchTrialWithSurveyAndGroundTruth,
  fetchTrialsList,
  clearSurveyRecord,
  updateSections,
  createSurvey,
  updateSurvey,
  reportError,
};

const connector = connect(mapStateToProps, dispatchProps);
type TypeFromRedux = ConnectedProps<typeof connector>;
type Props = TypeFromRedux & {
  intl: {
    formatMessage: (arg: Record<string, string>) => string;
  }
}

export default connector(injectIntl(CreateEditSurveyView));
